import { motion } from "framer-motion";
import { Reveal, EASE } from "@/lib/motion";
import { PageHero, PrimaryCTAs, SectionHeader, FaqBlock } from "@/components/site/PageParts";
import { CTABand } from "@/components/site/Bands";
import { useBooking } from "@/lib/booking";
import { ArrowRight } from "lucide-react";
import { SEO, breadcrumbSchema, faqSchema, serviceSchema } from "@/components/site/SEO";
import { RelatedWork } from "@/components/site/RelatedWork";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Blog Content", path: "/services/blog-content" },
];

const RELATED = [
  { tag: "Case Study", title: "Capillary Technologies — 2+ year partnership", desc: "Enterprise content execution across channels.", to: "/case-studies" },
  { tag: "Service", title: "Strategic Content Planning", desc: "The 90-day roadmap that feeds this engine.", to: "/services/content-strategy" },
  { tag: "Process", title: "The Dandelion Method", desc: "Why our articles keep compounding after publication.", to: "/how-we-work" },
];

const DIFFERENTIATORS = [
  { n: "01", title: "Strategic, not reactive", desc: "Every article serves a funnel purpose — not a random topic pulled from a keyword tool." },
  { n: "02", title: "Original research", desc: "SERP gaps, buyer conversations, and sales objections — not just what a tool spits out." },
  { n: "03", title: "Depth, not padding", desc: "2,000–3,500 words minimum, engineered for coverage, not word count." },
  { n: "04", title: "Technical accuracy", desc: "Writers who understand APIs, infrastructure, and security — not generic fluff." },
  { n: "05", title: "AI-search-optimized", desc: "Structured for ChatGPT, Perplexity, and Claude citation — not just Google." },
];

const INCLUDED = [
  { cat: "Content development", items: ["Strategic outline approved first", "Original research", "2,000–3,500 words", "Expert interviews where relevant", "Brand voice consistency"] },
  { cat: "SEO optimization", items: ["Keyword targeting", "Meta title / description", "Header optimization", "Internal linking", "Image optimization", "Schema markup"] },
  { cat: "AEO (AI engine optimization)", items: ["Citation-friendly structure", "Conversational query optimization", "Answer-extraction formatting"] },
  { cat: "Quality control", items: ["Plagiarism check", "Fact-checking", "Technical accuracy review", "Readability optimization"] },
];

const PROCESS = [
  { d: "Day 1–2", t: "Outline & approval", note: "Your approval needed." },
  { d: "Day 3–5", t: "First draft", note: "Written by a senior SaaS writer." },
  { d: "Day 6–7", t: "Client review", note: "Your feedback needed." },
  { d: "Day 8–9", t: "SEO optimization", note: "On-page + AEO polish." },
  { d: "Day 10", t: "QA & delivery", note: "Full quality-control checklist." },
];

const FAQS = [
  { q: "Do you use AI to write content?", a: "For research and efficiency, yes. For final drafts, no — every piece is written, edited, and fact-checked by humans." },
  { q: "Can you write in our brand voice?", a: "Yes, starting with a brand playbook. Most clients see it nailed within 2–3 articles." },
  { q: "What if we need revisions?", a: "Unlimited, until you're satisfied. 48-hour turnaround for minor changes, 72 hours for major rewrites." },
];

export default function BlogContent() {
  const { openBooking } = useBooking();
  return (
    <div data-testid="service-blog-page">
      <SEO
        title="SEO/AEO/GEO Blog Content for B2B SaaS"
        description="Research-backed articles engineered to rank in Google and get cited by ChatGPT, Perplexity, and Claude. 2,000–3,500 words. Written by senior SaaS writers."
        path="/services/blog-content"
        jsonLd={[breadcrumbSchema(CRUMBS), faqSchema(FAQS), serviceSchema({ name: "SEO-Optimized Blog Content", description: "Long-form articles engineered to rank in Google and be cited by AI search engines.", path: "/services/blog-content" })]}
      />
      <PageHero
        eyebrow="Services · Blog Content"
        lines={["Blog Content That", "Ranks in Google"]}
        italicLast="AND Gets Cited by AI."
        sub="Generic AI-written content is everywhere. Yours needs to be better. We create research-backed, strategically planned articles that rank in Google, get cited by ChatGPT and Perplexity, and actually convert readers into qualified leads."
        crumbs={CRUMBS}
      >
        <PrimaryCTAs />
      </PageHero>

      {/* Differentiators */}
      <section className="section-pad bg-[#F5F5F5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="Why it's different" title="Five things that separate our articles from AI slop." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 border-t border-l border-[#E0E0E0]">
            {DIFFERENTIATORS.map((d, i) => (
              <Reveal key={d.n} delay={i * 0.05}>
                <div className="p-8 border-r border-b border-[#E0E0E0] h-full">
                  <span className="font-mono text-xs text-[#999999]">{d.n}</span>
                  <h3 className="mt-5 font-serif text-xl text-[#111111]">{d.title}</h3>
                  <p className="mt-4 text-sm text-[#666666] leading-relaxed">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="What's included per article" title="Every article gets the same four-layer treatment." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14">
            {INCLUDED.map((sec, i) => (
              <Reveal key={sec.cat} delay={i * 0.06}>
                <div className="border-t border-[#111111] pt-6">
                  <h3 className="font-serif text-2xl md:text-3xl text-[#111111]">{sec.cat}</h3>
                  <ul className="mt-4">
                    {sec.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 py-2 border-b border-[#E0E0E0] text-[#111111]">
                        <span className="text-[#999999] font-mono text-sm mt-1">→</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 10-day process */}
      <section className="section-pad bg-[#111111] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#666666] mb-6 font-mono">Production · 10 days</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">Outline to shipped article in ten days.</h2>
          </Reveal>
          <div className="mt-14 border-t border-[#333333]">
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.d}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: EASE }}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 py-6 md:py-8 border-b border-[#333333]"
              >
                <p className="md:col-span-2 font-mono text-xs uppercase tracking-[0.2em] text-[#666666]">{p.d}</p>
                <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl text-white">{p.t}</h3>
                <p className="md:col-span-6 text-[#E0E0E0] leading-relaxed">{p.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment cards */}
      <section className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="Investment & options" title="Two ways to work with us." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal>
              <div className="border border-[#E0E0E0] p-8 md:p-10 h-full">
                <p className="text-xs uppercase tracking-[0.2em] text-[#999999]">One-time article</p>
                <p className="mt-4 font-serif text-4xl md:text-5xl text-[#111111]">$800–$1,200</p>
                <p className="mt-2 text-[#666666]">Per article · 7–10 day turnaround.</p>
                <p className="mt-6 text-[#111111] leading-relaxed">Good for testing our work or occasional needs.</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="bg-[#111111] text-white p-8 md:p-10 h-full relative">
                <span className="absolute top-6 right-6 border border-white px-3 py-1 text-[10px] uppercase tracking-[0.2em]">Recommended</span>
                <p className="text-xs uppercase tracking-[0.2em] text-[#999999]">Monthly retainer</p>
                <p className="mt-4 font-serif text-4xl md:text-5xl">$4,500–$15,000+</p>
                <p className="mt-2 text-[#999999]">Per month · 4–40 articles / month.</p>
                <p className="mt-6 text-[#E0E0E0] leading-relaxed">
                  Best for companies committed to content as a channel — lower per-article cost, strategic planning included, faster turnaround, consistent publishing.
                </p>
                <button type="button" onClick={openBooking} className="inline-flex items-center gap-2 mt-8 bg-white text-[#111111] font-semibold px-6 py-3 hover:bg-[#E0E0E0] transition-colors group" data-testid="blog-retainer-cta">
                  Schedule a Content Audit
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-[#F5F5F5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="FAQ" title="Common questions." />
          <div className="mt-12">
            <FaqBlock items={FAQS} />
          </div>
        </div>
      </section>

      <CTABand heading="Content buyers can't skip past." />
      <RelatedWork items={RELATED} />
    </div>
  );
}
